'use client';

import { SWRConfig } from 'swr';
import {
  isBridgeTokenEndpoint,
  isUnauthorizedError,
  recoverDeadSession,
} from '@/lib/auth/session-expiry';
import { onErrorRetry } from '@/lib/swr/on-error-retry';

interface SWRProviderProps {
  children: React.ReactNode;
}

export function SWRProvider({ children }: SWRProviderProps) {
  return (
    <SWRConfig
      value={{
        revalidateOnFocus: false,
        revalidateOnReconnect: true,
        dedupingInterval: 2000,
        shouldRetryOnError: true,
        errorRetryCount: 3,
        errorRetryInterval: 5000,
        // Stop the 401 retry storm: a terminal auth error (401/403) is never
        // retried instead of entering exponential backoff (ENG-1242).
        onErrorRetry,
        // When an authenticated read returns 401 the app session is gone, so
        // drop out of the zombie logged-in UI and back to login instead of
        // rendering stale client state. 403s are intentionally left alone
        // (authenticated but lacking access to a specific resource). Host-bridge
        // bearer-token endpoints (Rhino/Archicad) are skipped: their 401 means
        // the bridge token expired, not the app session, and they run their own
        // in-place reauth (ENG-1242).
        onError: (error, key) => {
          if (isUnauthorizedError(error) && !isBridgeTokenEndpoint(key)) {
            void recoverDeadSession();
          }
        },
        // Follow-up (ENG-1242): a hard hydration-time session probe was
        // considered so a cold palette start with stale client state lands on
        // login before rendering. It is intentionally omitted for now — hard
        // page loads already re-run server-side `auth()` gating, and the
        // `onError` handler above recovers the warm-webview case on the first
        // 401. Adding a probe here would duplicate better-auth's own session
        // fetch. Revisit if a zombie state is observed that neither path covers.
      }}
    >
      {children}
    </SWRConfig>
  );
}
